import { useCallback, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Records mic audio via MediaRecorder, sends it to /api/voice/transcribe
 *  (Whisper) and calls onText with the transcript. */
export function useVoiceInput({ onText, maxSeconds = 8 } = {}) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const recRef = useRef(null);
  const timerRef = useRef(null);

  const supported =
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof window !== "undefined" &&
    typeof window.MediaRecorder !== "undefined";

  const stop = useCallback(() => {
    if (recRef.current && recRef.current.state === "recording") recRef.current.stop();
  }, []);

  const start = useCallback(async () => {
    if (!supported || recording || busy) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = window.MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new window.MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
      rec.onstop = async () => {
        clearTimeout(timerRef.current);
        setRecording(false);
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: mime || "audio/webm" });
        if (blob.size < 800) return; // too short to contain speech
        setBusy(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          const { data } = await axios.post(`${API}/voice/transcribe`, fd);
          if (data?.text) onText?.(data.text);
        } catch (err) {
          console.warn("[voice] transcribe failed", err?.message);
        } finally {
          setBusy(false);
        }
      };
      recRef.current = rec;
      rec.start();
      setRecording(true);
      timerRef.current = setTimeout(() => {
        if (rec.state === "recording") rec.stop();
      }, maxSeconds * 1000);
    } catch (err) {
      console.warn("[voice] mic unavailable", err?.message);
    }
  }, [supported, recording, busy, onText, maxSeconds]);

  const toggle = useCallback(() => {
    if (recording) stop();
    else start();
  }, [recording, start, stop]);

  return { supported, recording, busy, start, stop, toggle };
}
